import Link from "next/link";
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from "react";

// Every card gets a punched hole at the top edge — this app's whole job is stamping
// consistent tags on a lot of items quickly, so the tag itself is the running motif.
export function TagCard({ children }: { children: ReactNode }) {
  return (
    <div className="relative">
      <div className="absolute left-1/2 -top-[9px] h-[18px] w-[18px] -translate-x-1/2 rounded-full border border-border bg-paper" />
      <div className="rounded-2xl border border-border bg-surface p-8 shadow-sm">
        {children}
      </div>
    </div>
  );
}

export function Wordmark() {
  return (
    <span className="inline-flex items-center gap-2 rounded-full bg-surface-tag px-3 py-1 font-mono text-xs font-semibold uppercase tracking-widest text-ink-muted">
      <span className="h-1.5 w-1.5 rounded-full bg-moss" />
      Household Lister
    </span>
  );
}

const buttonBase =
  "inline-flex items-center justify-center rounded-lg px-4 py-2.5 text-sm font-semibold font-mono transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-moss disabled:opacity-50 disabled:pointer-events-none";

const buttonVariants = {
  primary: "bg-moss text-moss-contrast hover:bg-moss-strong",
  ghost: "border border-border text-ink hover:bg-surface-tag",
};

export function buttonClasses(variant: keyof typeof buttonVariants = "primary", className = "") {
  return `${buttonBase} ${buttonVariants[variant]} ${className}`;
}

export function Button({
  variant = "primary",
  className = "",
  ...props
}: {
  variant?: keyof typeof buttonVariants;
  className?: string;
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button className={buttonClasses(variant, className)} {...props} />;
}

export function LinkButton({
  variant = "primary",
  className = "",
  href,
  children,
}: {
  variant?: keyof typeof buttonVariants;
  className?: string;
  href: string;
  children: ReactNode;
}) {
  return (
    <Link href={href} className={buttonClasses(variant, className)}>
      {children}
    </Link>
  );
}

export function Field({
  label,
  ...props
}: { label: string } & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
        {label}
      </span>
      <input
        className="w-full rounded-lg border border-border bg-paper px-3 py-2 text-sm text-ink placeholder:text-ink-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-moss"
        {...props}
      />
    </label>
  );
}

export function ErrorBanner({ children }: { children: ReactNode }) {
  return (
    <p className="rounded-lg border border-clay/30 bg-clay/10 px-3 py-2 text-sm text-clay">
      {children}
    </p>
  );
}
